"use client";

import React, { useCallback, useEffect } from "react";
import { Send } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useQuoteRequestForm } from "@/hooks/useQuoteRequestForm";
import { QUOTE_DIALOG, FORM_FIELDS, QUOTE_FORM_CONFIG } from "@/utils/constants/forms";
import type { QuoteRequestDialogProps } from "@/utils/types/forms";
import { THEME_COLORS } from "@/utils/constants/styles";

export function QuoteRequestDialog({
  isOpen,
  onOpenChange,
  title = QUOTE_DIALOG.TITLE,
  subtitle = QUOTE_DIALOG.SUBTITLE,
  defaultService,
  serviceOptions,
}: Readonly<QuoteRequestDialogProps>) {
  const closeDialog = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  const {
    form,
    options,
    propertyTypeOptions,
    isSubmitting,
    isLoadingOptions,
    handleSubmit,
    handleServiceChange,
    resetForm,
  } = useQuoteRequestForm({
    defaultService,
    serviceOptions,
    onSubmitSuccess: closeDialog,
  });

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleDialogChange = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange?.(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className={`text-2xl text-${THEME_COLORS.PRIMARY_GREEN} mb-4`}>
            {title}
          </DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            <input type="hidden" {...form.register("serviceName")} />
            <input type="hidden" {...form.register("redirectTo")} />

            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder={FORM_FIELDS.PLACEHOLDERS.FIRST_NAME}
                        className="py-3 bg-[var(--input-background)]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder={FORM_FIELDS.PLACEHOLDERS.LAST_NAME}
                        className="py-3 bg-[var(--input-background)]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={FORM_FIELDS.PLACEHOLDERS.EMAIL}
                        className="py-3 bg-[var(--input-background)]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder={FORM_FIELDS.PLACEHOLDERS.PHONE}
                        className="py-3 bg-[var(--input-background)]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={FORM_FIELDS.PLACEHOLDERS.ADDRESS}
                      className="py-3 bg-[var(--input-background)]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={FORM_FIELDS.PLACEHOLDERS.CITY}
                      className="py-3 bg-[var(--input-background)]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="serviceId"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      value={field.value || undefined}
                      disabled={isLoadingOptions}
                      onValueChange={(value) => {
                        field.onChange(value);
                        handleServiceChange(value);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="py-3 bg-[var(--input-background)]">
                          <SelectValue
                            placeholder={
                              isLoadingOptions
                                ? "Caricamento servizi..."
                                : FORM_FIELDS.PLACEHOLDERS.SERVICE_TYPE
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white">
                        {options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="propertyType"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      value={field.value || undefined}
                      onValueChange={(value) => field.onChange(value)}
                    >
                      <FormControl>
                        <SelectTrigger className="py-3 bg-[var(--input-background)]">
                          <SelectValue
                            placeholder={FORM_FIELDS.PLACEHOLDERS.PROPERTY_TYPE}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white">
                        {propertyTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="problemDescription"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder={FORM_FIELDS.PLACEHOLDERS.PROBLEM_DESCRIPTION}
                      className="min-h-24 bg-[var(--input-background)]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="privacyAccepted"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-start space-x-3">
                    <FormControl>
                      <input
                        type="checkbox"
                        id="privacy-popup"
                        className="mt-1.5"
                        checked={field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    </FormControl>
                    <label
                      htmlFor="privacy-popup"
                      className={`text-sm text-${THEME_COLORS.GRAY_SECONDARY} leading-relaxed`}
                    >
                      {QUOTE_FORM_CONFIG.PRIVACY_TEXT}
                    </label>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-${THEME_COLORS.PRIMARY_GREEN} hover:bg-${THEME_COLORS.PRIMARY_GREEN_HOVER} text-${THEME_COLORS.WHITE} py-3 transition-transform duration-150 active:scale-[0.97]`}
            >
              {isSubmitting ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {isSubmitting ? QUOTE_DIALOG.LOADING_TEXT : QUOTE_FORM_CONFIG.SUBMIT_BUTTON_TEXT}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}